import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import VerifiedTierBadge from "./VerifiedTierBadge";
import TrustScoreBadge from "./TrustScoreBadge";
import { hideOnError } from "../lib/imgFallback";
import { Sparkles, Wand2, Loader2, Bell, BellRing, X } from "lucide-react";
import { displayName, displayInitial } from "../lib/displayName";

const SUGGESTIONS = [
  "Yoruba Ifá practitioner specializing in ancestral work",
  "Vodou priest for protection rituals, Verified Elder",
  "Tarot reader with 5+ years experience, Spanish speaker",
  "Quimbanda Angola practitioner for court-case support",
];

/**
 * AIMatchSearch — natural-language search bar that posts to
 * POST /discover/ai-match and renders the top 5 with a "matchReason"
 * explanation string per result. Embedded at the top of /discover.
 */
export default function AIMatchSearch() {
  const [prompt, setPrompt] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState([]);

  const loadSaved = async () => {
    try {
      const { data } = await api.get("/saved-searches/me");
      setSaved(data.searches || []);
    } catch (e) { /* unauthenticated is fine */ }
  };
  useEffect(() => { loadSaved(); }, []);

  const saveCurrent = async () => {
    if (!results) return;
    setSaving(true);
    try {
      const { data } = await api.post("/saved-searches", {
        prompt: prompt.trim(),
        parsedFilter: results.parsed,
        label: prompt.trim().slice(0, 60),
      });
      toast.success(data.alreadySaved ? "You already saved this search" : "Saved — we'll email you when matches appear");
      loadSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const removeSaved = async (sid) => {
    try {
      await api.delete(`/saved-searches/${sid}`);
      toast.success("Saved search removed");
      loadSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const search = async (p) => {
    const q = (p ?? prompt).trim();
    if (q.length < 4) {
      toast.error("Type at least a few words about what you're looking for");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/discover/ai-match", { prompt: q, limit: 5 });
      setResults(data);
      if (p !== undefined) setPrompt(q);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-violet-300/30 bg-violet-400/[0.04] rounded-sm p-5 space-y-4" data-testid="ai-match-search">
      <div>
        <div className="inline-flex items-center gap-2 text-violet-200">
          <Wand2 size={14} strokeWidth={1.5} />
          <span className="label-mystic">Ask in plain words</span>
        </div>
        <p className="text-sm text-foreground/70 mt-1.5">
          Describe what you need — we'll match by tradition, lineage, specialty, and Trust Score.
        </p>
      </div>

      <div className="flex gap-2">
        <input
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") search(); }}
          placeholder="Yoruba Ifá practitioner specializing in ancestral work..."
          className="flex-1 bg-input border border-secondary/20 px-3 py-2.5 rounded-sm focus:outline-none focus:border-violet-300 text-sm"
          data-testid="ai-search-input"
        />
        <button
          onClick={() => search()}
          disabled={busy}
          className="inline-flex items-center gap-1.5 bg-violet-300 text-background px-5 py-2.5 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
          data-testid="ai-search-submit"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {busy ? "Searching..." : "Find"}
        </button>
      </div>

      {!results && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          <span className="text-[10px] uppercase tracking-[0.18em] text-foreground/45 w-full">Try</span>
          {SUGGESTIONS.map((s, i) => (
            <button
              key={i}
              onClick={() => search(s)}
              className="text-[11px] text-violet-200/85 hover:text-violet-200 border border-violet-300/25 rounded-full px-3 py-1"
              data-testid={`ai-suggestion-${i}`}
            >
              "{s.slice(0, 36)}{s.length > 36 ? "…" : ""}"
            </button>
          ))}
        </div>
      )}

      {results && (
        <div className="space-y-3 pt-2" data-testid="ai-results">
          {results.parsed && (
            <div className="text-[11px] text-foreground/55 border border-secondary/15 rounded-sm p-2.5" data-testid="ai-parsed">
              <span className="label-mystic">Understood as · </span>
              {results.parsed.tradition && <span className="text-violet-200">{results.parsed.tradition} · </span>}
              {(results.parsed.specialties || []).join(", ") || <span className="italic text-foreground/40">no specialty</span>}
              {results.parsed.minTier && results.parsed.minTier !== "none" && (
                <span className="text-foreground/65"> · min tier {results.parsed.minTier}</span>
              )}
              {results.parsed.minYearsInitiated && (
                <span className="text-foreground/65"> · {results.parsed.minYearsInitiated}+ yrs</span>
              )}
            </div>
          )}

          {results.matches.length === 0 && (
            <div className="parchment p-6 text-center text-foreground/55 italic" data-testid="ai-empty">
              No matches yet. Try widening your tradition or removing the minimum-tier requirement.
            </div>
          )}

          {results.matches.map((p, i) => (
            <Link
              key={p.userId}
              to={`/profile/${p.userId}`}
              className="parchment p-4 flex items-start gap-4 hover:border-secondary/40 transition-colors block"
              data-testid={`ai-match-${p.userId}`}
            >
              <div className="font-serif text-2xl text-secondary w-8 flex-shrink-0">{i + 1}</div>
              <div className="w-12 h-12 rounded-full overflow-hidden bg-card border border-secondary/20 flex-shrink-0">
                {(p.avatarUrl || p.user?.avatarUrl) ? (
                  <img src={p.avatarUrl || p.user?.avatarUrl} alt="" onError={hideOnError} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-serif text-lg text-secondary">
                    {displayInitial(p)}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-serif text-lg truncate">{displayName(p)}</div>
                  <VerifiedTierBadge tier={p.verifiedTier} size={11} compact />
                  <TrustScoreBadge score={p.trustScore || 0} compact />
                </div>
                {p.tradition && (
                  <div className="text-[11px] text-foreground/60 mt-0.5">
                    <Sparkles size={9} className="inline -mt-0.5 mr-1 text-violet-300/80" />
                    {p.tradition}
                  </div>
                )}
                <div className="text-[11px] text-violet-200/80 mt-1.5" data-testid={`ai-match-reason-${p.userId}`}>
                  Match: {p.matchReason}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-serif text-2xl text-secondary">{p.matchScore?.toFixed(0)}</div>
                <div className="label-mystic text-[10px]">match</div>
              </div>
            </Link>
          ))}

          <button
            onClick={() => { setResults(null); setPrompt(""); }}
            className="text-[11px] uppercase tracking-[0.16em] text-foreground/55 hover:text-secondary"
            data-testid="ai-clear"
          >Clear · Browse manually →</button>

          <button
            onClick={saveCurrent}
            disabled={saving}
            className="ml-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-violet-200 hover:text-violet-100 disabled:opacity-50"
            data-testid="ai-save-search"
          >
            <Bell size={11} strokeWidth={1.5} />
            {saving ? "Saving..." : "Save & alert me when new matches appear"}
          </button>
        </div>
      )}

      {saved.length > 0 && !results && (
        <div className="pt-3 border-t border-violet-300/15 space-y-1.5" data-testid="ai-saved-list">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-foreground/55">
            <BellRing size={11} strokeWidth={1.5} /> Saved searches ({saved.length})
          </div>
          {saved.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 text-xs text-foreground/70" data-testid={`saved-search-${s.id}`}>
              <button
                onClick={() => { setPrompt(s.prompt); search(s.prompt); }}
                className="text-left hover:text-violet-200 truncate flex-1"
              >
                "{s.label || s.prompt}"
                {s.totalMatchesSent > 0 && <span className="text-secondary ml-1">· {s.totalMatchesSent} matched</span>}
              </button>
              <button
                onClick={() => removeSaved(s.id)}
                className="text-foreground/40 hover:text-orange-300"
                data-testid={`saved-search-delete-${s.id}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
